/**
 * Shared resolver for TAS flattened custom-field values.
 *
 * OpenSearch documents can surface TAS custom fields under different container keys
 * and leaf key formats across index versions. This utility normalizes container aliases
 * and leaf naming variants so selected report columns consistently render a value.
 */

type RecordValue = Record<string, unknown>;

const APPLICANT_CONTAINER_ALIASES = [
  'tasApplicantCustomFieldsFlattened',
  'tas_applicant_custom_fields_flattened',
] as const;

const PROGRAM_INSTANCE_CONTAINER_ALIASES = [
  'tasProgramInstanceCustomFieldsFlattened',
  'tas_program_instance_custom_fields_flattened',
] as const;

const APPLICATION_CONTAINER_ALIASES = [
  'tasApplicationCustomFieldsFlattened',
  'tas_application_custom_fields_flattened',
] as const;

const COURSE_CONTAINER_ALIASES = [
  'tasCourseCustomFieldsFlattened',
  'tas_course_custom_fields_flattened',
] as const;

function snakeToCamelCase(value: string): string {
  return value.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase());
}

function camelToSnakeCase(value: string): string {
  return value.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`).replace(/^_/, '');
}

function normalizeLookupKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function normalizeContainerKey(containerKey: string): string {
  return containerKey.replace(/_/g, '').toLowerCase();
}

function isPresentValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

function canonicalTasCustomLeaf(containerKey: string, rawLeaf: string): string | null {
  const normalizedContainer = normalizeContainerKey(containerKey);
  const normalizedLeaf = normalizeLookupKey(rawLeaf);

  if (
    normalizedContainer === 'tasprograminstancecustomfieldsflattened' ||
    normalizedContainer === 'tasapplicantcustomfieldsflattened'
  ) {
    const syntheticMatch = normalizedLeaf.match(/customfieldtas(?:programinstance|applicant)(\d+)$/);
    if (syntheticMatch?.[1]) {
      return `customFieldTasProgramInstance_${syntheticMatch[1]}`;
    }

    const legacyMatch = normalizedLeaf.match(
      /tas(?:programinstance|applicant)customfield(?:tas(?:programinstance|applicant))?(\d+)$/
    );
    if (legacyMatch?.[1]) {
      return `customFieldTasProgramInstance_${legacyMatch[1]}`;
    }
  }

  if (normalizedContainer === 'tasapplicationcustomfieldsflattened') {
    const syntheticMatch = normalizedLeaf.match(/customfieldtasapplication(\d+)$/);
    if (syntheticMatch?.[1]) {
      return `customFieldTasApplication_${syntheticMatch[1]}`;
    }

    const legacyMatch = normalizedLeaf.match(/tasapplicationcustomfield(?:tasapplication)?(\d+)$/);
    if (legacyMatch?.[1]) {
      return `customFieldTasApplication_${legacyMatch[1]}`;
    }
  }

  if (normalizedContainer === 'tascoursecustomfieldsflattened') {
    const syntheticMatch = normalizedLeaf.match(/customfieldtascourse(\d+)$/);
    if (syntheticMatch?.[1]) {
      return `customFieldTasCourse_${syntheticMatch[1]}`;
    }

    const legacyMatch = normalizedLeaf.match(/tascoursecustomfield(?:tascourse)?(\d+)$/);
    if (legacyMatch?.[1]) {
      return `customFieldTasCourse_${legacyMatch[1]}`;
    }
  }

  return null;
}

function baseContainerAliases(containerKey: string): string[] {
  const normalized = normalizeContainerKey(containerKey);

  if (normalized === 'tasapplicantcustomfieldsflattened') {
    return [...APPLICANT_CONTAINER_ALIASES, ...PROGRAM_INSTANCE_CONTAINER_ALIASES];
  }

  if (normalized === 'tasprograminstancecustomfieldsflattened') {
    return [...PROGRAM_INSTANCE_CONTAINER_ALIASES, ...APPLICANT_CONTAINER_ALIASES];
  }

  if (normalized === 'tasapplicationcustomfieldsflattened') {
    return [...APPLICATION_CONTAINER_ALIASES];
  }

  if (normalized === 'tascoursecustomfieldsflattened') {
    return [...COURSE_CONTAINER_ALIASES];
  }

  return [containerKey];
}

function containerKeyVariants(containerKey: string): string[] {
  const variants = new Set<string>();

  baseContainerAliases(containerKey).forEach((alias) => {
    variants.add(alias);
    variants.add(snakeToCamelCase(alias));
    variants.add(camelToSnakeCase(alias));
  });

  return Array.from(variants);
}

function containerRecords(item: RecordValue, containerKey: string): Array<[string, RecordValue]> {
  const records: Array<[string, RecordValue]> = [];

  containerKeyVariants(containerKey).forEach((candidateKey) => {
    const value = item[candidateKey];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      records.push([candidateKey, value as RecordValue]);
    }
  });

  return records;
}

export function tasCustomFieldValue(item: RecordValue, columnKey: string): unknown {
  const firstDot = columnKey.indexOf('.');
  if (firstDot < 0) {
    return undefined;
  }

  const containerKey = columnKey.slice(0, firstDot);
  const leafKey = columnKey.slice(firstDot + 1);
  if (!containerKey || !leafKey) {
    return undefined;
  }

  const rawLeaf = leafKey.split('.').filter(Boolean).at(-1) || leafKey;
  const normalizedLeaf = normalizeLookupKey(rawLeaf);
  const canonicalLeaf = canonicalTasCustomLeaf(containerKey, rawLeaf);

  for (const [resolvedContainerKey, container] of containerRecords(item, containerKey)) {
    const entries = Object.entries(container);

    const match = entries.find(([candidateKey, candidateValue]) => {
      if (!isPresentValue(candidateValue)) {
        return false;
      }

      const candidateLeaf = candidateKey.split('.').filter(Boolean).at(-1) || candidateKey;
      const normalizedCandidateLeaf = normalizeLookupKey(candidateLeaf);
      if (normalizedCandidateLeaf === normalizedLeaf) {
        return true;
      }

      const canonicalCandidateLeaf = canonicalTasCustomLeaf(resolvedContainerKey, candidateLeaf);
      if (canonicalLeaf && canonicalCandidateLeaf && canonicalLeaf === canonicalCandidateLeaf) {
        return true;
      }

      const normalizedCandidate = normalizeLookupKey(candidateKey);
      return normalizedCandidate.endsWith(normalizedLeaf);
    });

    if (match) {
      return match[1];
    }
  }

  return undefined;
}
